// ═══════════════════════════════════════════════════════════════════════
// Bellanor Alta Performance — Design Tokens v2 (TEMA ESCURO)
//
// Paleta extraída pixel-a-pixel do Canva "Projeto de Vida Bellanor 2026"
// Base: #1a140f (preto quente com undertone marrom-café)
// Marca: #785040 marrom Bellanor (6,6% da tela no deck)
// Metais: extraídos dos próprios troféus (não genéricos)
//
// Mobile-first: BP.mobile (< 640px) colapsa grids, ajusta typography
// ═══════════════════════════════════════════════════════════════════════

export const T = {
  // ── Backgrounds ────────────────────────────────────────────────────
  bg:         "#1a140f",   // preto quente principal (Canva dominante 45%)
  bgDeep:     "#100800",   // preto profundo (vignette, modais)
  surface:    "#201810",   // header, nav, barras superiores
  card:       "#282018",   // cards principais (elev 1)
  cardSoft:   "#201810",   // cards alternativos (elev 2, mais afundado)
  cardHi:     "#302820",   // cards hover / destaque
  elev:       "#3a2e26",   // elementos elevados (inputs dentro de cards)
  input:      "#1f1812",   // inputs
  overlay:    "rgba(0,0,0,0.70)",  // modal overlay

  // ── Borders ────────────────────────────────────────────────────────
  border:     "#3a2e26",   // bordas suaves sobre preto
  borderMid:  "#4a3d32",   // bordas médias
  borderHi:   "#6b5846",   // hover / focus

  // ── Texto (escala em bege/cinza warm) ──────────────────────────────
  text:       "#f5ede3",   // branco quente principal (não #fff puro)
  textSec:    "#d9cfc2",   // secundário
  textSub:    "#a69684",   // sutil (labels)
  textDim:    "#7a6d5e",   // muito sutil (timestamps, hints)
  textInv:    "#1a140f",   // sobre fundos claros/dourados

  // ── Brand — marrom café Bellanor ───────────────────────────────────
  brand:      "#b4876d",   // marrom claro pra ficar legível sobre preto
  brandHi:    "#c49a80",   // hover (mais claro ainda)
  brandDim:   "#785040",   // marrom tradicional Bellanor (usado em acentos)
  brandSoft:  "#3a2a20",   // versão escura (fundo de badge)
  brandTint:  "#b4876d",

  // ── Dourado metálico (Canva — "Você vai Prosperar" vibe) ──────────
  gold:       "#d7be7c",   // dourado fosco do troféu Golden
  goldHi:     "#e8cf8e",   // dourado claro (títulos hero)
  goldDim:    "#9a8048",   // dourado escuro (sombras)

  // ── Award colors (extraídas dos PNGs reais dos badges) ────────────
  bronze:     "#b8652b",   // cobre médio (entre centro e brilho do PNG)
  silver:     "#cdcdcd",   // prata puro
  gold2:      "#d7be7c",   // mesmo do gold acima
  platinum:   "#6692ad",   // azul aço
  diamond:    "#c6bfb9",   // prata polido

  // ── Semânticos (ajustados pra legibilidade em dark) ────────────────
  ok:         "#6ab07d",   // verde vivo
  okSoft:     "#1e2e22",   // verde muito escuro (fundo)
  warn:       "#d9a455",   // âmbar dourado
  warnSoft:   "#332516",
  err:        "#d47066",   // vermelho suave
  errSoft:    "#331916",
  info:       "#7ba3c9",   // azul suave
  infoSoft:   "#1a2330",

  // ── Layout ─────────────────────────────────────────────────────────
  r:  { sm: 4, md: 8, lg: 12, xl: 16, xxl: 20 },
  ease: "cubic-bezier(.4,0,.2,1)",
  shadow: {
    sm: "0 1px 2px rgba(0,0,0,0.4)",
    md: "0 4px 12px rgba(0,0,0,0.5), 0 2px 4px rgba(0,0,0,0.3)",
    lg: "0 12px 32px rgba(0,0,0,0.6), 0 4px 8px rgba(0,0,0,0.4)",
    brand: "0 4px 16px rgba(180,135,109,0.25)",
    gold: "0 0 24px rgba(215,190,124,0.15)",
    glow: "0 0 40px rgba(180,135,109,0.2)",
  },

  // ── Gradientes signature (inspirados no Canva) ─────────────────────
  gradient: {
    // Background principal — radial quente ao centro
    bgRadial: "radial-gradient(ellipse 80% 60% at 50% 30%, #2a1f17 0%, #1a140f 55%, #100800 100%)",
    // Card hero (perfil, nacional, hero geral)
    cardHero: "linear-gradient(135deg, #3a2a20 0%, #282018 40%, #1a140f 100%)",
    // Brand CTA (botão primary destaque)
    brandCta: "linear-gradient(135deg, #b4876d 0%, #785040 100%)",
    // Dourado premium (elementos de destaque raro — Diamond for Life, BYD)
    goldPremium: "linear-gradient(135deg, #e8cf8e 0%, #d7be7c 50%, #9a8048 100%)",
  },

  // ── Breakpoints mobile-first ──────────────────────────────────────
  bp: {
    mobile: 640,    // <= 640px = mobile
    tablet: 900,    // <= 900px = tablet
  },
};

// ─── AWARD_COLORS ajustados para tema escuro ──────────────────────────
// Backgrounds escuros com borda do metal, texto na cor do metal
export const AWARD_COLORS = {
  cafe:     { bg: "#2a2018",  fg: "#a69684", border: "#4a3d32" },
  bronze:   { bg: "#2a1814",  fg: "#d18855", border: "#8a4d24" },
  silver:   { bg: "#222222",  fg: "#e0e0e0", border: "#808080" },
  gold:     { bg: "#2a2416",  fg: "#e8cf8e", border: "#a88a48" },
  platinum: { bg: "#1a2430",  fg: "#8ab5d0", border: "#4a7394" },
  diamond:  { bg: "#262030",  fg: "#d0c4e0", border: "#7a6aa0" },
};

// ─── Storage helpers ─────────────────────────────────────────────────
export function load(key, fallback) {
  try {
    const v = localStorage.getItem(key);
    return v ? JSON.parse(v) : fallback;
  } catch { return fallback; }
}

export function save(key, data) {
  try { localStorage.setItem(key, JSON.stringify(data)); } catch {}
}

// ─── Formatadores ────────────────────────────────────────────────────
export const fmt = {
  brl:     (n) => `R$ ${Number(n || 0).toLocaleString("pt-BR", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`,
  brlFull: (n) => `R$ ${Number(n || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`,
  pct:     (n) => `${Number(n || 0).toFixed(0)}%`,
  num:     (n) => Number(n || 0).toLocaleString("pt-BR"),
};

// ─── Hook para detectar mobile (usar em componentes) ─────────────────
import { useState, useEffect } from "react";

export function useIsMobile() {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth <= T.bp.mobile : false
  );

  useEffect(() => {
    if (typeof window === "undefined") return;
    const handler = () => setIsMobile(window.innerWidth <= T.bp.mobile);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  return isMobile;
}
